import { Box, FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react'
import type { PropsWithChildren, ReactElement } from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isNull, isUndefined } from 'lodash'
import { IKReactWindowedSelect } from '../../../../../ui-kit'
import { TypeBadge } from '../../../../../utils/mediaLibrary'
import BulkEditModeDropdown from './BulkEditModeDropdown'
import type { CMFieldProps } from '.'

const CMSingleSelectField = ({
  cmField,
  disabled,
  readOnly,
  placeholder,
  isUpload,
  form,
  children,
  labelElement,
  showBulkEditFieldModeDropdown,
  bulkEditFieldMode,
  style,
  required,
  menuPosition,
  setBulkEditFieldMode,
  onInputChange,
}: PropsWithChildren<CMFieldProps>): ReactElement => {
  const { t } = useTranslation()
  const [cmFieldSelectOptions, setCmFieldSelectOptions] =
    useState<Array<string | number | boolean>>()

  const shouldShowFormField = useMemo(() => {
    if (!showBulkEditFieldModeDropdown) {
      return true
    }

    return bulkEditFieldMode?.[cmField.name] === 'set'
  }, [bulkEditFieldMode, cmField.name, showBulkEditFieldModeDropdown])

  useEffect(() => {
    setCmFieldSelectOptions(cmField.schema.selectOptions ?? [])
  }, [form?.values?.upload, cmField])

  const formattedValue = useMemo(() => {
    const isDisplayDisabled = readOnly || disabled

    return (
      !(
        isUndefined(form?.values?.upload[cmField.name]) ||
        isNull(form?.values?.upload[cmField.name])
      ) && {
        label: isDisplayDisabled ? (
          String(form?.values?.upload[cmField.name])
        ) : (
          <>
            {String(form?.values?.upload[cmField.name])}{' '}
            <TypeBadge val={form?.values?.upload[cmField.name]} />
          </>
        ),
        value: form?.values?.upload[cmField.name],
      }
    )
  }, [form?.values?.upload[cmField.name], disabled, readOnly, cmField.name, cmField])

  const options = useMemo(() => {
    return cmFieldSelectOptions?.map(option => ({
      label: String(option),
      value: option,
    }))
  }, [cmFieldSelectOptions])

  const formattedPlaceholder = useMemo(() => {
    if (
      !(
        isUndefined(cmField.schema.defaultValue) ||
        isNull(cmField.schema.defaultValue)
      ) &&
      isUpload
    ) {
      return `${cmField.schema.defaultValue}[${typeof cmField.schema
        .defaultValue}] (Default value)`
    }

    return ''
  }, [cmField.schema.defaultValue, isUpload, cmField])

  const onChange = useCallback(
    (newVal: { label: string; value: string }) => {
      form.setTouched({ ...form.touched, [cmField.name]: true })
      if (isNull(newVal)) {
        form.setFieldValue(`upload.['${cmField.name}']`, null)
      } else {
        form.setFieldValue(`upload.['${cmField.name}']`, newVal.value)
      }
      onInputChange?.(cmField, form, newVal ? newVal.value : undefined)
    },
    [form, cmField.name, onInputChange],
  )

  return (
    <FormControl
      marginBottom={isUpload ? 0 : '30px'}
      isInvalid={Boolean(
        form.errors?.upload &&
          form.touched?.upload &&
          form.errors?.upload[cmField.name] &&
          form.touched?.upload[cmField.name],
      )}
      isRequired={cmField.schema.isValueRequired}
      isDisabled={disabled}
      style={style}
    >
      {labelElement ?? <FormLabel>{cmField.label}</FormLabel>}
      <Box width="100%">
        {showBulkEditFieldModeDropdown &&
          bulkEditFieldMode &&
          setBulkEditFieldMode && (
            <BulkEditModeDropdown
              bulkEditFieldMode={bulkEditFieldMode}
              setBulkEditFieldMode={setBulkEditFieldMode}
              cmField={cmField}
              disabled={readOnly || disabled}
              menuPosition={menuPosition}
              onInputChange={() => {
                onInputChange?.(cmField, form)
              }}
            />
          )}
        {shouldShowFormField && (
          <>
            <IKReactWindowedSelect
              placeholder={formattedPlaceholder || placeholder || t('select_a_value', 'Select a value')}
              variant="compact"
              captureMenuScroll={false}
              menuPlacement="auto"
              isClearable
              isDisabled={readOnly || disabled}
              value={formattedValue}
              options={options}
              onChange={onChange}
              required={required}
              menuPosition={menuPosition}
            />
            <FormErrorMessage>
              {form.errors?.upload && form.errors?.upload[cmField.name]}
            </FormErrorMessage>
          </>
        )}
      </Box>
      {children}
    </FormControl>
  )
}

export default CMSingleSelectField
